<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="mb-6">
        <div class="flex items-center gap-3 mb-2">
          <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">대시보드</h1>
            <p class="text-sm text-slate-500">{{ todayDate }} 기준</p>
          </div>
        </div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
      </div>

      <div v-else>
        <!-- ==================== 긴급 대기 섹션 ==================== -->
        <div v-if="totalPending > 0" class="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl p-5 mb-6 text-white">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h2 class="text-lg font-bold">승인 대기 {{ totalPending }}건</h2>
              <p class="text-sm text-white/80">즉시 처리가 필요합니다</p>
            </div>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            <router-link v-if="pending.partners > 0" to="/admin/partner-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.partners }}</p>
              <p class="text-xs text-white/80">파트너 신청</p>
            </router-link>
            <router-link v-if="pending.pointCharges > 0" to="/admin/point-charges" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.pointCharges }}</p>
              <p class="text-xs text-white/80">포인트 충전</p>
            </router-link>
            <router-link v-if="pending.adBanners > 0" to="/admin/adbanner-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.adBanners }}</p>
              <p class="text-xs text-white/80">인앱 광고</p>
            </router-link>
            <router-link v-if="pending.popups > 0" to="/admin/popup-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.popups }}</p>
              <p class="text-xs text-white/80">팝업 광고</p>
            </router-link>
            <router-link v-if="pending.splashes > 0" to="/admin/splash-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.splashes }}</p>
              <p class="text-xs text-white/80">스플래시</p>
            </router-link>
            <router-link v-if="pending.recommends > 0" to="/admin/recommend-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.recommends }}</p>
              <p class="text-xs text-white/80">추천 광고</p>
            </router-link>
            <router-link v-if="pending.listAds > 0" to="/admin/listad-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.listAds }}</p>
              <p class="text-xs text-white/80">리스트 광고</p>
            </router-link>
            <router-link v-if="pending.communityAds > 0" to="/admin/communityad-applications" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.communityAds }}</p>
              <p class="text-xs text-white/80">커뮤니티 광고</p>
            </router-link>
            <router-link v-if="pending.reports > 0" to="/admin/community-reports" class="bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition">
              <p class="text-2xl font-bold">{{ pending.reports }}</p>
              <p class="text-xs text-white/80">신고 처리</p>
            </router-link>
          </div>
        </div>

        <!-- ==================== 오늘의 현황 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"/>
            </svg>
            오늘의 현황
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-blue-600">{{ today.signups }}</p>
              <p class="text-xs text-slate-600 mt-1">신규 가입</p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-emerald-600">{{ today.consultations }}</p>
              <p class="text-xs text-slate-600 mt-1">상담 신청</p>
            </div>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-violet-600">{{ today.properties }}</p>
              <p class="text-xs text-slate-600 mt-1">신규 현장</p>
            </div>
            <div class="bg-rose-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-rose-600">{{ today.posts }}</p>
              <p class="text-xs text-slate-600 mt-1">커뮤니티 글</p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-amber-600">{{ formatNumber(today.chargedPoints) }}</p>
              <p class="text-xs text-slate-600 mt-1">충전된 포인트</p>
            </div>
            <div class="bg-cyan-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-cyan-600">{{ formatNumber(today.usedPoints) }}</p>
              <p class="text-xs text-slate-600 mt-1">사용된 포인트</p>
            </div>
            <div class="bg-indigo-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-indigo-600">{{ today.jumps }}</p>
              <p class="text-xs text-slate-600 mt-1">점프 구매</p>
            </div>
            <div class="bg-orange-50 rounded-xl p-4 text-center">
              <p class="text-2xl font-bold text-orange-600">{{ today.campaigns }}</p>
              <p class="text-xs text-slate-600 mt-1">신규 캠페인</p>
            </div>
          </div>
        </div>

        <!-- ==================== 핵심 통계 ==================== -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <!-- 전체 현장 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">전체 현장</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.properties }}</p>
            <p class="text-xs text-slate-500 mt-1">분양중 {{ stats.onSaleProperties }}</p>
          </div>

          <!-- 전체 사용자 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">전체 사용자</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.users }}</p>
            <p class="text-xs text-slate-500 mt-1">정지 {{ stats.suspendedUsers }}</p>
          </div>

          <!-- 파트너 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">파트너</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.partners }}</p>
            <p class="text-xs text-slate-500 mt-1">이번주 +{{ stats.weekPartners }}</p>
          </div>

          <!-- 전체 상담 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">전체 상담</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.consultations }}</p>
            <p class="text-xs text-slate-500 mt-1">대기 {{ consultationStats.pending }}</p>
          </div>

          <!-- 커뮤니티 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">커뮤니티</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.posts }}</p>
            <p class="text-xs text-slate-500 mt-1">댓글 {{ stats.comments }}</p>
          </div>

          <!-- 캠페인 -->
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                </svg>
              </div>
              <span class="text-xs text-slate-500">캠페인</span>
            </div>
            <p class="text-2xl font-bold text-slate-800">{{ stats.campaigns }}</p>
            <p class="text-xs text-slate-500 mt-1">활성 {{ stats.activeCampaigns }}</p>
          </div>
        </div>

        <!-- ==================== 상담 신청 현황 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            상담 신청 현황
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <div class="bg-amber-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">대기 중</p>
              <p class="text-2xl font-bold text-amber-600">{{ consultationStats.pending }}</p>
            </div>
            <div class="bg-blue-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">연락 완료</p>
              <p class="text-2xl font-bold text-blue-600">{{ consultationStats.contacted }}</p>
            </div>
            <div class="bg-red-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">유실 (24시간 초과)</p>
              <p class="text-2xl font-bold text-red-600">{{ consultationStats.expired }}</p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">선착순 픽업</p>
              <p class="text-2xl font-bold text-emerald-600">{{ consultationStats.picked }}</p>
            </div>
            <div class="bg-violet-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">캠페인 유입</p>
              <p class="text-2xl font-bold text-violet-600">{{ consultationStats.fromCampaign }}</p>
            </div>
            <div class="bg-orange-50 rounded-xl p-4">
              <p class="text-xs text-slate-600 mb-1">광고 유입 대기</p>
              <p class="text-2xl font-bold text-orange-600">{{ consultationStats.bannerPending }}</p>
            </div>
          </div>
        </div>

        <!-- ==================== 포인트 & 점프 현황 ==================== -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- 포인트 현황 -->
          <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              포인트 현황
            </h2>
            <div class="space-y-3">
              <div class="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span class="text-slate-600">총 충전액</span>
                <span class="text-lg font-bold text-emerald-600">{{ formatNumber(pointStats.totalCharged) }}P</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span class="text-slate-600">총 사용액</span>
                <span class="text-lg font-bold text-blue-600">{{ formatNumber(pointStats.totalUsed) }}P</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span class="text-slate-600">총 환불액</span>
                <span class="text-lg font-bold text-red-600">{{ formatNumber(pointStats.totalRefunded) }}P</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <span class="text-slate-700 font-medium">파트너 총 잔액</span>
                <span class="text-xl font-bold text-emerald-600">{{ formatNumber(pointStats.totalBalance) }}P</span>
              </div>
            </div>
          </div>

          <!-- 점프 현황 -->
          <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              점프 현황
            </h2>
            <div class="space-y-3">
              <div class="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span class="text-slate-600">전체 점프</span>
                <span class="text-lg font-bold text-slate-800">{{ jumpStats.total }}건</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-violet-50 rounded-xl">
                <span class="text-slate-600">현재 활성 점프</span>
                <span class="text-lg font-bold text-violet-600">{{ jumpStats.active }}건</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span class="text-slate-600">취소된 점프</span>
                <span class="text-lg font-bold text-red-600">{{ jumpStats.cancelled }}건</span>
              </div>
              <div class="flex justify-between items-center p-3 bg-blue-50 rounded-xl border border-blue-100">
                <span class="text-slate-700 font-medium">점프 총 수익</span>
                <span class="text-xl font-bold text-blue-600">{{ formatNumber(jumpStats.totalRevenue) }}P</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 광고 현황 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
            </svg>
            광고 현황 (활성/전체)
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div class="bg-indigo-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">인앱 광고</p>
              <p class="text-xl font-bold text-indigo-600">{{ adStats.adBanners.active }}<span class="text-sm text-slate-400">/{{ adStats.adBanners.total }}</span></p>
            </div>
            <div class="bg-rose-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">팝업 광고</p>
              <p class="text-xl font-bold text-rose-600">{{ adStats.popups.active }}<span class="text-sm text-slate-400">/{{ adStats.popups.total }}</span></p>
            </div>
            <div class="bg-cyan-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">스플래시</p>
              <p class="text-xl font-bold text-cyan-600">{{ adStats.splashes.active }}<span class="text-sm text-slate-400">/{{ adStats.splashes.total }}</span></p>
            </div>
            <div class="bg-orange-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">추천 광고</p>
              <p class="text-xl font-bold text-orange-600">{{ adStats.recommends.active }}<span class="text-sm text-slate-400">/{{ adStats.recommends.total }}</span></p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">리스트 광고</p>
              <p class="text-xl font-bold text-emerald-600">{{ adStats.listAds.active }}<span class="text-sm text-slate-400">/{{ adStats.listAds.total }}</span></p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">커뮤니티 광고</p>
              <p class="text-xl font-bold text-amber-600">{{ adStats.communityAds.active }}<span class="text-sm text-slate-400">/{{ adStats.communityAds.total }}</span></p>
            </div>
          </div>
        </div>

        <!-- ==================== 이번 주 & 이번 달 비교 ==================== -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- 이번 주 통계 -->
          <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 class="text-lg font-bold text-slate-800 mb-4">이번 주</h2>
            <div class="grid grid-cols-2 gap-3">
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">신규 가입</p>
                <p class="text-xl font-bold text-slate-800">{{ weekStats.signups }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">상담 신청</p>
                <p class="text-xl font-bold text-slate-800">{{ weekStats.consultations }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">충전 포인트</p>
                <p class="text-xl font-bold text-emerald-600">{{ formatNumber(weekStats.chargedPoints) }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">점프 구매</p>
                <p class="text-xl font-bold text-violet-600">{{ weekStats.jumps }}</p>
              </div>
            </div>
          </div>

          <!-- 이번 달 통계 -->
          <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 class="text-lg font-bold text-slate-800 mb-4">이번 달</h2>
            <div class="grid grid-cols-2 gap-3">
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">신규 가입</p>
                <p class="text-xl font-bold text-slate-800">{{ monthStats.signups }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">상담 신청</p>
                <p class="text-xl font-bold text-slate-800">{{ monthStats.consultations }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">충전 포인트</p>
                <p class="text-xl font-bold text-emerald-600">{{ formatNumber(monthStats.chargedPoints) }}</p>
              </div>
              <div class="p-3 bg-slate-50 rounded-xl">
                <p class="text-xs text-slate-500">점프 구매</p>
                <p class="text-xl font-bold text-violet-600">{{ monthStats.jumps }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 파트너 관리 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            파트너 관리
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <router-link to="/admin/partner-applications" class="bg-violet-50 hover:bg-violet-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">소속 신청 대기</p>
              <p class="text-2xl font-bold text-violet-600">{{ partnerManagement.pendingApplications }}</p>
            </router-link>
            <router-link to="/admin/point-charges" class="bg-emerald-50 hover:bg-emerald-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">포인트 충전 대기</p>
              <p class="text-2xl font-bold text-emerald-600">{{ partnerManagement.pendingCharges }}</p>
            </router-link>
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">총 파트너 수</p>
              <p class="text-2xl font-bold text-blue-600">{{ partnerManagement.totalPartners }}</p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">활성 파트너</p>
              <p class="text-2xl font-bold text-amber-600">{{ partnerManagement.activePartners }}</p>
            </div>
            <div class="bg-rose-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">현장 미배정</p>
              <p class="text-2xl font-bold text-rose-600">{{ partnerManagement.unassignedPartners }}</p>
            </div>
            <router-link to="/admin/partner-notices" class="bg-slate-50 hover:bg-slate-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">파트너 공지</p>
              <p class="text-2xl font-bold text-slate-600">{{ partnerManagement.notices }}</p>
            </router-link>
          </div>
          <!-- 최근 신청 파트너 -->
          <div v-if="recentPartnerApps.length > 0" class="bg-slate-50 rounded-xl p-4">
            <p class="text-xs text-slate-500 mb-2">최근 소속 신청</p>
            <div class="space-y-2">
              <div v-for="app in recentPartnerApps" :key="app.id" class="flex items-center justify-between text-sm">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-slate-700">{{ app.name }}</span>
                  <span class="text-slate-400">→</span>
                  <span class="text-slate-600">{{ app.propertyName }}</span>
                </div>
                <span class="text-xs text-slate-400">{{ app.timeAgo }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 서비스 관리 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            서비스 관리
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <router-link to="/admin/notices" class="bg-blue-50 hover:bg-blue-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">공지사항</p>
              <p class="text-2xl font-bold text-blue-600">{{ serviceManagement.notices }}</p>
            </router-link>
            <router-link to="/admin/events" class="bg-rose-50 hover:bg-rose-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">진행중 이벤트</p>
              <p class="text-2xl font-bold text-rose-600">{{ serviceManagement.activeEvents }}</p>
            </router-link>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">총 이벤트</p>
              <p class="text-2xl font-bold text-emerald-600">{{ serviceManagement.totalEvents }}</p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">이벤트 참여</p>
              <p class="text-2xl font-bold text-amber-600">{{ serviceManagement.eventParticipants }}</p>
            </div>
            <router-link to="/admin/banners" class="bg-indigo-50 hover:bg-indigo-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">메인 배너</p>
              <p class="text-2xl font-bold text-indigo-600">{{ serviceManagement.mainBanners }}</p>
            </router-link>
            <router-link to="/admin/magazines" class="bg-cyan-50 hover:bg-cyan-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">매거진</p>
              <p class="text-2xl font-bold text-cyan-600">{{ serviceManagement.magazines }}</p>
            </router-link>
            <router-link to="/admin/hot-ad" class="bg-orange-50 hover:bg-orange-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">HOT AD</p>
              <p class="text-2xl font-bold text-orange-600">{{ serviceManagement.hotAd ? 1 : 0 }}</p>
            </router-link>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">포인트 상품</p>
              <p class="text-2xl font-bold text-violet-600">{{ serviceManagement.pointProducts }}</p>
            </div>
          </div>
        </div>

        <!-- ==================== 고객센터 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            고객센터
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <router-link to="/admin/inquiries" class="bg-rose-50 hover:bg-rose-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">문의 대기중</p>
              <p class="text-2xl font-bold text-rose-600">{{ support.pendingInquiries }}</p>
            </router-link>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">답변 완료</p>
              <p class="text-2xl font-bold text-emerald-600">{{ support.answeredInquiries }}</p>
            </div>
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">총 문의</p>
              <p class="text-2xl font-bold text-blue-600">{{ support.totalInquiries }}</p>
            </div>
            <router-link to="/admin/faq" class="bg-amber-50 hover:bg-amber-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">FAQ</p>
              <p class="text-2xl font-bold text-amber-600">{{ support.faqCount }}</p>
            </router-link>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">오늘 문의</p>
              <p class="text-2xl font-bold text-violet-600">{{ support.todayInquiries }}</p>
            </div>
            <div class="bg-cyan-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">평균 응답시간</p>
              <p class="text-2xl font-bold text-cyan-600">{{ support.avgResponseTime }}h</p>
            </div>
          </div>
          <!-- 최근 문의 -->
          <div v-if="recentInquiries.length > 0" class="bg-slate-50 rounded-xl p-4">
            <p class="text-xs text-slate-500 mb-2">최근 문의</p>
            <div class="space-y-2">
              <div v-for="inq in recentInquiries" :key="inq.id" class="flex items-center justify-between text-sm">
                <div class="flex items-center gap-2">
                  <span :class="[
                    'px-2 py-0.5 rounded text-xs font-medium',
                    inq.status === 'pending' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                  ]">{{ inq.status === 'pending' ? '대기' : '완료' }}</span>
                  <span class="text-slate-700 truncate max-w-[200px]">{{ inq.title }}</span>
                </div>
                <span class="text-xs text-slate-400">{{ inq.timeAgo }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 커뮤니티 관리 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
            </svg>
            커뮤니티 관리
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">전체 게시글</p>
              <p class="text-2xl font-bold text-blue-600">{{ communityManagement.totalPosts }}</p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">오늘 게시글</p>
              <p class="text-2xl font-bold text-emerald-600">{{ communityManagement.todayPosts }}</p>
            </div>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">전체 댓글</p>
              <p class="text-2xl font-bold text-violet-600">{{ communityManagement.totalComments }}</p>
            </div>
            <div class="bg-cyan-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">오늘 댓글</p>
              <p class="text-2xl font-bold text-cyan-600">{{ communityManagement.todayComments }}</p>
            </div>
            <router-link to="/admin/community-reports" class="bg-rose-50 hover:bg-rose-100 rounded-xl p-4 text-center transition">
              <p class="text-xs text-slate-600 mb-1">신고 대기</p>
              <p class="text-2xl font-bold text-rose-600">{{ communityManagement.pendingReports }}</p>
            </router-link>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">처리된 신고</p>
              <p class="text-2xl font-bold text-amber-600">{{ communityManagement.processedReports }}</p>
            </div>
            <div class="bg-slate-100 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">블라인드</p>
              <p class="text-2xl font-bold text-slate-600">{{ communityManagement.blindedPosts }}</p>
            </div>
            <div class="bg-indigo-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">좋아요 수</p>
              <p class="text-2xl font-bold text-indigo-600">{{ formatNumber(communityManagement.totalLikes) }}</p>
            </div>
          </div>
          <!-- 카테고리별 통계 -->
          <div class="bg-slate-50 rounded-xl p-4">
            <p class="text-xs text-slate-500 mb-3">카테고리별 게시글</p>
            <div class="grid grid-cols-5 gap-2">
              <div v-for="cat in communityManagement.categories" :key="cat.id" class="text-center">
                <p class="text-lg font-bold text-slate-700">{{ cat.count }}</p>
                <p class="text-xs text-slate-500">{{ cat.label }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 현장 상세 관리 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            현장 상세 관리
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">전체 현장</p>
              <p class="text-2xl font-bold text-blue-600">{{ propertyManagement.total }}</p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">분양중</p>
              <p class="text-2xl font-bold text-emerald-600">{{ propertyManagement.onSale }}</p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">분양예정</p>
              <p class="text-2xl font-bold text-amber-600">{{ propertyManagement.preSale }}</p>
            </div>
            <div class="bg-slate-100 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">분양완료</p>
              <p class="text-2xl font-bold text-slate-600">{{ propertyManagement.completed }}</p>
            </div>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">소속 상담사</p>
              <p class="text-2xl font-bold text-violet-600">{{ propertyManagement.assignedPartners }}</p>
            </div>
            <div class="bg-rose-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">현장 댓글</p>
              <p class="text-2xl font-bold text-rose-600">{{ propertyManagement.propertyComments }}</p>
            </div>
          </div>
          <!-- 지역별 현장 -->
          <div class="bg-slate-50 rounded-xl p-4">
            <p class="text-xs text-slate-500 mb-3">지역별 현장</p>
            <div class="flex flex-wrap gap-2">
              <div v-for="region in propertyManagement.regions" :key="region.name" class="px-3 py-1.5 bg-white rounded-lg text-sm">
                <span class="font-medium text-slate-700">{{ region.name }}</span>
                <span class="ml-1 text-blue-600 font-bold">{{ region.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== 사용자 상세 관리 섹션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            사용자 상세 관리
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <div class="bg-blue-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">전체 사용자</p>
              <p class="text-2xl font-bold text-blue-600">{{ userManagement.total }}</p>
            </div>
            <div class="bg-emerald-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">일반 회원</p>
              <p class="text-2xl font-bold text-emerald-600">{{ userManagement.users }}</p>
            </div>
            <div class="bg-violet-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">파트너</p>
              <p class="text-2xl font-bold text-violet-600">{{ userManagement.partners }}</p>
            </div>
            <div class="bg-indigo-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">관리자</p>
              <p class="text-2xl font-bold text-indigo-600">{{ userManagement.admins }}</p>
            </div>
            <div class="bg-rose-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">정지됨</p>
              <p class="text-2xl font-bold text-rose-600">{{ userManagement.suspended }}</p>
            </div>
            <div class="bg-amber-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">이번 주 가입</p>
              <p class="text-2xl font-bold text-amber-600">{{ userManagement.weekSignups }}</p>
            </div>
            <div class="bg-cyan-50 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">이번 달 가입</p>
              <p class="text-2xl font-bold text-cyan-600">{{ userManagement.monthSignups }}</p>
            </div>
            <div class="bg-slate-100 rounded-xl p-4 text-center">
              <p class="text-xs text-slate-600 mb-1">탈퇴 회원</p>
              <p class="text-2xl font-bold text-slate-600">{{ userManagement.deleted }}</p>
            </div>
          </div>
        </div>

        <!-- ==================== 수익 현황 섹션 ==================== -->
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 text-white mb-6">
          <h2 class="text-lg font-bold mb-4 flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            수익 현황
          </h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div class="bg-white/10 rounded-xl p-4">
              <p class="text-xs text-white/70 mb-1">총 포인트 충전</p>
              <p class="text-2xl font-bold">{{ formatNumber(revenueStats.totalCharged) }}P</p>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <p class="text-xs text-white/70 mb-1">이번 달 충전</p>
              <p class="text-2xl font-bold">{{ formatNumber(revenueStats.monthCharged) }}P</p>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <p class="text-xs text-white/70 mb-1">점프 수익</p>
              <p class="text-2xl font-bold">{{ formatNumber(revenueStats.jumpRevenue) }}P</p>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <p class="text-xs text-white/70 mb-1">캠페인 수익</p>
              <p class="text-2xl font-bold">{{ formatNumber(revenueStats.campaignRevenue) }}P</p>
            </div>
          </div>
        </div>

        <!-- ==================== 빠른 액션 ==================== -->
        <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <h2 class="text-lg font-bold text-slate-800 mb-4">빠른 액션</h2>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <router-link to="/admin/properties/create" class="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              <span class="text-xs font-medium text-blue-700">새 현장</span>
            </router-link>
            <router-link to="/admin/properties" class="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition">
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
              </svg>
              <span class="text-xs font-medium text-slate-700">현장 관리</span>
            </router-link>
            <router-link to="/admin/users" class="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition">
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197"/>
              </svg>
              <span class="text-xs font-medium text-slate-700">사용자</span>
            </router-link>
            <router-link to="/admin/banners" class="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition">
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span class="text-xs font-medium text-slate-700">배너</span>
            </router-link>
            <router-link to="/admin/partner-applications" class="flex flex-col items-center gap-2 p-4 bg-violet-50 hover:bg-violet-100 rounded-xl transition">
              <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span class="text-xs font-medium text-violet-700">파트너 승인</span>
            </router-link>
            <router-link to="/admin/point-charges" class="flex flex-col items-center gap-2 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition">
              <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
              </svg>
              <span class="text-xs font-medium text-emerald-700">충전 승인</span>
            </router-link>
            <router-link to="/admin/community-reports" class="flex flex-col items-center gap-2 p-4 bg-rose-50 hover:bg-rose-100 rounded-xl transition">
              <svg class="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <span class="text-xs font-medium text-rose-700">신고 처리</span>
            </router-link>
            <router-link to="/admin/inquiries" class="flex flex-col items-center gap-2 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition">
              <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span class="text-xs font-medium text-amber-700">문의 답변</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'AdminHomeView',
  components: {
    AdminLayout
  },
  setup() {
    const loading = ref(true)

    // 오늘 날짜 표시
    const todayDate = computed(() => {
      const now = new Date()
      return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일`
    })

    // 기본 통계
    const stats = ref({
      properties: 0,
      onSaleProperties: 0,
      users: 0,
      suspendedUsers: 0,
      partners: 0,
      weekPartners: 0,
      consultations: 0,
      posts: 0,
      comments: 0,
      campaigns: 0,
      activeCampaigns: 0
    })

    // 오늘 통계
    const today = ref({
      signups: 0,
      consultations: 0,
      properties: 0,
      posts: 0,
      chargedPoints: 0,
      usedPoints: 0,
      jumps: 0,
      campaigns: 0
    })

    // 이번 주 통계
    const weekStats = ref({
      signups: 0,
      consultations: 0,
      chargedPoints: 0,
      jumps: 0
    })

    // 이번 달 통계
    const monthStats = ref({
      signups: 0,
      consultations: 0,
      chargedPoints: 0,
      jumps: 0
    })

    // 대기 중 카운트
    const pending = ref({
      partners: 0,
      pointCharges: 0,
      adBanners: 0,
      popups: 0,
      splashes: 0,
      recommends: 0,
      listAds: 0,
      communityAds: 0,
      reports: 0
    })

    // 상담 신청 상태별
    const consultationStats = ref({
      pending: 0,
      contacted: 0,
      expired: 0,
      picked: 0,
      fromCampaign: 0,
      bannerPending: 0
    })

    // 포인트 통계
    const pointStats = ref({
      totalCharged: 0,
      totalUsed: 0,
      totalRefunded: 0,
      totalBalance: 0
    })

    // 점프 통계
    const jumpStats = ref({
      total: 0,
      active: 0,
      cancelled: 0,
      totalRevenue: 0
    })

    // 광고 통계
    const adStats = ref({
      adBanners: { active: 0, total: 0 },
      popups: { active: 0, total: 0 },
      splashes: { active: 0, total: 0 },
      recommends: { active: 0, total: 0 },
      listAds: { active: 0, total: 0 },
      communityAds: { active: 0, total: 0 }
    })

    // 파트너 관리
    const partnerManagement = ref({
      pendingApplications: 0,
      pendingCharges: 0,
      totalPartners: 0,
      activePartners: 0,
      unassignedPartners: 0,
      notices: 0
    })

    // 최근 파트너 신청
    const recentPartnerApps = ref([])

    // 서비스 관리
    const serviceManagement = ref({
      notices: 0,
      activeEvents: 0,
      totalEvents: 0,
      eventParticipants: 0,
      mainBanners: 0,
      magazines: 0,
      hotAd: false,
      pointProducts: 0
    })

    // 고객센터
    const support = ref({
      pendingInquiries: 0,
      answeredInquiries: 0,
      totalInquiries: 0,
      faqCount: 0,
      todayInquiries: 0,
      avgResponseTime: 0
    })

    // 최근 문의
    const recentInquiries = ref([])

    // 커뮤니티 관리
    const communityManagement = ref({
      totalPosts: 0,
      todayPosts: 0,
      totalComments: 0,
      todayComments: 0,
      pendingReports: 0,
      processedReports: 0,
      blindedPosts: 0,
      totalLikes: 0,
      categories: [
        { id: 'free', label: '자유', count: 0 },
        { id: 'question', label: '질문', count: 0 },
        { id: 'review', label: '분양후기', count: 0 },
        { id: 'info', label: '정보공유', count: 0 },
        { id: 'tip', label: '꿀팁', count: 0 }
      ]
    })

    // 현장 상세 관리
    const propertyManagement = ref({
      total: 0,
      onSale: 0,
      preSale: 0,
      completed: 0,
      assignedPartners: 0,
      propertyComments: 0,
      regions: []
    })

    // 사용자 상세 관리
    const userManagement = ref({
      total: 0,
      users: 0,
      partners: 0,
      admins: 0,
      suspended: 0,
      weekSignups: 0,
      monthSignups: 0,
      deleted: 0
    })

    // 수익 현황
    const revenueStats = ref({
      totalCharged: 0,
      monthCharged: 0,
      jumpRevenue: 0,
      campaignRevenue: 0
    })

    const totalPending = computed(() => {
      return pending.value.partners + pending.value.pointCharges +
        pending.value.adBanners + pending.value.popups + pending.value.splashes +
        pending.value.recommends + pending.value.listAds + pending.value.communityAds +
        pending.value.reports
    })

    const formatNumber = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toLocaleString()
    }

    // 날짜 헬퍼
    const getToday = () => {
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      koreaTime.setHours(0, 0, 0, 0)
      return koreaTime.toISOString()
    }

    const getWeekStart = () => {
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      const day = koreaTime.getDay()
      const diff = koreaTime.getDate() - day + (day === 0 ? -6 : 1)
      koreaTime.setDate(diff)
      koreaTime.setHours(0, 0, 0, 0)
      return koreaTime.toISOString()
    }

    const getMonthStart = () => {
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      koreaTime.setDate(1)
      koreaTime.setHours(0, 0, 0, 0)
      return koreaTime.toISOString()
    }

    const getNowISO = () => new Date().toISOString()

    const fetchAllStats = async () => {
      try {
        const todayISO = getToday()
        const weekStartISO = getWeekStart()
        const monthStartISO = getMonthStart()
        const nowISO = getNowISO()

        // 1. 현장 통계
        const propertiesSnap = await getDocs(collection(db, 'properties'))
        stats.value.properties = propertiesSnap.size
        let onSale = 0, todayProps = 0
        propertiesSnap.forEach(doc => {
          const d = doc.data()
          if (d.status === 'on-sale') onSale++
          if (d.createdAt >= todayISO) todayProps++
        })
        stats.value.onSaleProperties = onSale
        today.value.properties = todayProps

        // 2. 사용자 통계
        const usersSnap = await getDocs(collection(db, 'users'))
        stats.value.users = usersSnap.size
        let suspended = 0, partners = 0, weekPartners = 0, todaySignups = 0, weekSignups = 0, monthSignups = 0
        usersSnap.forEach(doc => {
          const d = doc.data()
          if (d.isSuspended) suspended++
          if (d.role === 'partner') {
            partners++
            if (d.createdAt >= weekStartISO) weekPartners++
          }
          if (d.createdAt >= todayISO) todaySignups++
          if (d.createdAt >= weekStartISO) weekSignups++
          if (d.createdAt >= monthStartISO) monthSignups++
        })
        stats.value.suspendedUsers = suspended
        stats.value.partners = partners
        stats.value.weekPartners = weekPartners
        today.value.signups = todaySignups
        weekStats.value.signups = weekSignups
        monthStats.value.signups = monthSignups

        // 3. 상담 신청 통계
        const consultSnap = await getDocs(collection(db, 'consultationRequests'))
        stats.value.consultations = consultSnap.size
        let todayConsult = 0, weekConsult = 0, monthConsult = 0
        let cPending = 0, cContacted = 0, cExpired = 0, cPicked = 0, cCampaign = 0, cBanner = 0
        consultSnap.forEach(doc => {
          const d = doc.data()
          if (d.createdAt >= todayISO) todayConsult++
          if (d.createdAt >= weekStartISO) weekConsult++
          if (d.createdAt >= monthStartISO) monthConsult++
          if (d.status === 'pending') cPending++
          if (d.status === 'contacted') cContacted++
          if (d.status === 'expired') cExpired++
          if (d.status === 'picked') cPicked++
          if (d.status === 'banner_pending') cBanner++
          if (d.fromCampaign) cCampaign++
        })
        today.value.consultations = todayConsult
        weekStats.value.consultations = weekConsult
        monthStats.value.consultations = monthConsult
        consultationStats.value = { pending: cPending, contacted: cContacted, expired: cExpired, picked: cPicked, fromCampaign: cCampaign, bannerPending: cBanner }

        // 4. 커뮤니티 통계
        const postsSnap = await getDocs(collection(db, 'communityPosts'))
        stats.value.posts = postsSnap.size
        let todayPosts = 0
        postsSnap.forEach(doc => {
          if (doc.data().createdAt >= todayISO) todayPosts++
        })
        today.value.posts = todayPosts

        const commentsSnap = await getDocs(collection(db, 'communityComments'))
        stats.value.comments = commentsSnap.size

        // 5. 캠페인 통계
        const campaignsSnap = await getDocs(collection(db, 'campaigns'))
        stats.value.campaigns = campaignsSnap.size
        let activeCampaigns = 0, todayCampaigns = 0
        campaignsSnap.forEach(doc => {
          const d = doc.data()
          if (d.status === 'active' && d.endDate >= nowISO.split('T')[0]) activeCampaigns++
          if (d.createdAt >= todayISO) todayCampaigns++
        })
        stats.value.activeCampaigns = activeCampaigns
        today.value.campaigns = todayCampaigns

        // 6. 포인트 통계
        const pointsSnap = await getDocs(collection(db, 'partnerPoints'))
        let totalBalance = 0, totalCharged = 0, totalUsed = 0
        pointsSnap.forEach(doc => {
          const d = doc.data()
          totalBalance += d.balance || 0
          totalCharged += d.totalCharged || 0
          totalUsed += d.totalUsed || 0
        })
        pointStats.value.totalBalance = totalBalance
        pointStats.value.totalCharged = totalCharged
        pointStats.value.totalUsed = totalUsed

        // 포인트 거래 내역
        const transSnap = await getDocs(collection(db, 'pointTransactions'))
        let refunded = 0, todayCharged = 0, todayUsed = 0, weekCharged = 0, monthCharged = 0
        transSnap.forEach(doc => {
          const d = doc.data()
          if (d.type === 'refund') refunded += Math.abs(d.amount || 0)
          if (d.type === 'charge' && d.createdAt >= todayISO) todayCharged += d.amount || 0
          if (d.type === 'use' && d.createdAt >= todayISO) todayUsed += Math.abs(d.amount || 0)
          if (d.type === 'charge' && d.createdAt >= weekStartISO) weekCharged += d.amount || 0
          if (d.type === 'charge' && d.createdAt >= monthStartISO) monthCharged += d.amount || 0
        })
        pointStats.value.totalRefunded = refunded
        today.value.chargedPoints = todayCharged
        today.value.usedPoints = todayUsed
        weekStats.value.chargedPoints = weekCharged
        monthStats.value.chargedPoints = monthCharged

        // 7. 점프 통계
        const jumpsSnap = await getDocs(collection(db, 'jumpSlots'))
        let totalJumps = 0, activeJumps = 0, cancelledJumps = 0, jumpRevenue = 0
        let todayJumps = 0, weekJumps = 0, monthJumps = 0
        jumpsSnap.forEach(doc => {
          const d = doc.data()
          totalJumps++
          if (d.status === 'active') activeJumps++
          if (d.status === 'cancelled') cancelledJumps++
          jumpRevenue += d.totalCoins || 0
          if (d.createdAt >= todayISO) todayJumps++
          if (d.createdAt >= weekStartISO) weekJumps++
          if (d.createdAt >= monthStartISO) monthJumps++
        })
        jumpStats.value = { total: totalJumps, active: activeJumps, cancelled: cancelledJumps, totalRevenue: jumpRevenue }
        today.value.jumps = todayJumps
        weekStats.value.jumps = weekJumps
        monthStats.value.jumps = monthJumps

        // 8. 대기 중 승인
        const pendingQueries = [
          { col: 'partnerApplications', key: 'partners' },
          { col: 'pointChargeRequests', key: 'pointCharges' },
          { col: 'adBanners', key: 'adBanners' },
          { col: 'popupAds', key: 'popups' },
          { col: 'splashAds', key: 'splashes' },
          { col: 'recommendAds', key: 'recommends' },
          { col: 'listAds', key: 'listAds' },
          { col: 'communityAds', key: 'communityAds' }
        ]
        for (const pq of pendingQueries) {
          try {
            const snap = await getDocs(query(collection(db, pq.col), where('status', '==', 'pending')))
            pending.value[pq.key] = snap.size
          } catch { pending.value[pq.key] = 0 }
        }

        // 신고 대기
        try {
          const reportsSnap = await getDocs(query(collection(db, 'communityReports'), where('status', '==', 'pending')))
          pending.value.reports = reportsSnap.size
        } catch { pending.value.reports = 0 }

        // 9. 광고 통계
        const adCollections = [
          { col: 'adBanners', key: 'adBanners' },
          { col: 'popupAds', key: 'popups' },
          { col: 'splashAds', key: 'splashes' },
          { col: 'recommendAds', key: 'recommends' },
          { col: 'listAds', key: 'listAds' },
          { col: 'communityAds', key: 'communityAds' }
        ]
        for (const ac of adCollections) {
          try {
            const snap = await getDocs(collection(db, ac.col))
            let active = 0
            snap.forEach(doc => {
              const d = doc.data()
              if (d.status === 'approved' && d.expiresAt && d.expiresAt >= nowISO) active++
            })
            adStats.value[ac.key] = { active, total: snap.size }
          } catch {
            adStats.value[ac.key] = { active: 0, total: 0 }
          }
        }

        // 10. 파트너 관리 통계
        partnerManagement.value.pendingApplications = pending.value.partners
        partnerManagement.value.pendingCharges = pending.value.pointCharges
        partnerManagement.value.totalPartners = partners

        // 활성 파트너 (현장 배정된 파트너)
        try {
          const ppSnap = await getDocs(query(collection(db, 'propertyPartners'), where('status', '==', 'active')))
          const assignedPartnerIds = new Set()
          ppSnap.forEach(doc => assignedPartnerIds.add(doc.data().partnerId))
          partnerManagement.value.activePartners = assignedPartnerIds.size
          partnerManagement.value.assignedPartners = ppSnap.size
          propertyManagement.value.assignedPartners = ppSnap.size
        } catch {
          partnerManagement.value.activePartners = 0
          propertyManagement.value.assignedPartners = 0
        }

        // 미배정 파트너
        partnerManagement.value.unassignedPartners = partners - partnerManagement.value.activePartners

        // 파트너 공지사항
        try {
          const pnSnap = await getDocs(collection(db, 'partnerNotices'))
          partnerManagement.value.notices = pnSnap.size
        } catch { partnerManagement.value.notices = 0 }

        // 최근 파트너 신청 (최근 5개)
        try {
          const appsSnap = await getDocs(collection(db, 'partnerApplications'))
          const apps = []
          appsSnap.forEach(doc => {
            const d = doc.data()
            if (d.status === 'pending') {
              apps.push({ id: doc.id, ...d })
            }
          })
          apps.sort((a, b) => b.createdAt?.localeCompare(a.createdAt) || 0)
          recentPartnerApps.value = apps.slice(0, 5).map(a => ({
            id: a.id,
            name: a.name || a.partnerEmail?.split('@')[0] || '알 수 없음',
            propertyName: a.propertyName || '미정',
            timeAgo: formatTimeAgo(a.createdAt)
          }))
        } catch { recentPartnerApps.value = [] }

        // 11. 서비스 관리 통계
        // 공지사항
        try {
          const noticesSnap = await getDocs(collection(db, 'notices'))
          serviceManagement.value.notices = noticesSnap.size
        } catch { serviceManagement.value.notices = 0 }

        // 이벤트
        try {
          const eventsSnap = await getDocs(collection(db, 'events'))
          let activeEvents = 0
          let participants = 0
          eventsSnap.forEach(doc => {
            const d = doc.data()
            if (d.status === 'active' || (d.endDate && d.endDate >= nowISO.split('T')[0])) activeEvents++
            participants += d.participantCount || 0
          })
          serviceManagement.value.totalEvents = eventsSnap.size
          serviceManagement.value.activeEvents = activeEvents
          serviceManagement.value.eventParticipants = participants
        } catch {
          serviceManagement.value.totalEvents = 0
          serviceManagement.value.activeEvents = 0
          serviceManagement.value.eventParticipants = 0
        }

        // 메인 배너
        try {
          const bannersSnap = await getDocs(query(collection(db, 'mainBanners'), where('isActive', '==', true)))
          serviceManagement.value.mainBanners = bannersSnap.size
        } catch { serviceManagement.value.mainBanners = 0 }

        // 매거진
        try {
          const magSnap = await getDocs(collection(db, 'magazines'))
          serviceManagement.value.magazines = magSnap.size
        } catch { serviceManagement.value.magazines = 0 }

        // HOT AD
        try {
          const hotAdSnap = await getDocs(collection(db, 'hotAd'))
          serviceManagement.value.hotAd = hotAdSnap.size > 0
        } catch { serviceManagement.value.hotAd = false }

        // 포인트 상품
        try {
          const productsSnap = await getDocs(collection(db, 'pointProducts'))
          serviceManagement.value.pointProducts = productsSnap.size
        } catch { serviceManagement.value.pointProducts = 0 }

        // 12. 고객센터 통계
        try {
          const inquiriesSnap = await getDocs(collection(db, 'inquiries'))
          let pInq = 0, aInq = 0, todayInq = 0
          const inquiries = []
          inquiriesSnap.forEach(doc => {
            const d = doc.data()
            if (d.status === 'pending') pInq++
            else aInq++
            if (d.createdAt >= todayISO) todayInq++
            inquiries.push({ id: doc.id, ...d })
          })
          support.value.pendingInquiries = pInq
          support.value.answeredInquiries = aInq
          support.value.totalInquiries = inquiriesSnap.size
          support.value.todayInquiries = todayInq
          support.value.avgResponseTime = aInq > 0 ? 2 : 0 // 임시값

          // 최근 문의 5개
          inquiries.sort((a, b) => b.createdAt?.localeCompare(a.createdAt) || 0)
          recentInquiries.value = inquiries.slice(0, 5).map(i => ({
            id: i.id,
            title: i.title || '제목 없음',
            status: i.status,
            timeAgo: formatTimeAgo(i.createdAt)
          }))
        } catch {
          support.value = { pendingInquiries: 0, answeredInquiries: 0, totalInquiries: 0, faqCount: 0, todayInquiries: 0, avgResponseTime: 0 }
          recentInquiries.value = []
        }

        // FAQ
        try {
          const faqSnap = await getDocs(collection(db, 'faqs'))
          support.value.faqCount = faqSnap.size
        } catch { support.value.faqCount = 0 }

        // 13. 커뮤니티 관리 통계
        communityManagement.value.totalPosts = stats.value.posts
        communityManagement.value.todayPosts = today.value.posts
        communityManagement.value.totalComments = stats.value.comments

        // 오늘 댓글
        try {
          const todayCommentsSnap = await getDocs(collection(db, 'communityComments'))
          let tComments = 0
          todayCommentsSnap.forEach(doc => {
            if (doc.data().createdAt >= todayISO) tComments++
          })
          communityManagement.value.todayComments = tComments
        } catch { communityManagement.value.todayComments = 0 }

        // 신고 통계
        try {
          const reportsSnap = await getDocs(collection(db, 'communityReports'))
          let pReports = 0, processedReports = 0
          reportsSnap.forEach(doc => {
            const d = doc.data()
            if (d.status === 'pending') pReports++
            else processedReports++
          })
          communityManagement.value.pendingReports = pReports
          communityManagement.value.processedReports = processedReports
        } catch {
          communityManagement.value.pendingReports = 0
          communityManagement.value.processedReports = 0
        }

        // 블라인드 게시글
        try {
          const blindedSnap = await getDocs(query(collection(db, 'communityPosts'), where('isBlinded', '==', true)))
          communityManagement.value.blindedPosts = blindedSnap.size
        } catch { communityManagement.value.blindedPosts = 0 }

        // 좋아요 수
        try {
          const likesSnap = await getDocs(collection(db, 'communityLikes'))
          communityManagement.value.totalLikes = likesSnap.size
        } catch { communityManagement.value.totalLikes = 0 }

        // 카테고리별 게시글 수
        try {
          const postsData = []
          postsSnap.forEach(doc => postsData.push(doc.data()))
          const catCounts = { free: 0, question: 0, review: 0, info: 0, tip: 0 }
          postsData.forEach(p => {
            if (catCounts[p.category] !== undefined) catCounts[p.category]++
          })
          communityManagement.value.categories = [
            { id: 'free', label: '자유', count: catCounts.free },
            { id: 'question', label: '질문', count: catCounts.question },
            { id: 'review', label: '분양후기', count: catCounts.review },
            { id: 'info', label: '정보공유', count: catCounts.info },
            { id: 'tip', label: '꿀팁', count: catCounts.tip }
          ]
        } catch {
          // 커뮤니티 통계 로드 실패 무시
        }

        // 14. 현장 상세 관리 통계
        propertyManagement.value.total = stats.value.properties
        propertyManagement.value.onSale = stats.value.onSaleProperties

        // 분양예정, 분양완료
        let preSale = 0, completed = 0
        const regionCounts = {}
        propertiesSnap.forEach(doc => {
          const d = doc.data()
          if (d.status === 'pre-sale') preSale++
          if (d.status === 'completed') completed++
          const region = d.regionMain || d.region || '기타'
          regionCounts[region] = (regionCounts[region] || 0) + 1
        })
        propertyManagement.value.preSale = preSale
        propertyManagement.value.completed = completed
        propertyManagement.value.regions = Object.entries(regionCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10)

        // 현장 댓글
        try {
          const propCommentsSnap = await getDocs(collection(db, 'propertyComments'))
          propertyManagement.value.propertyComments = propCommentsSnap.size
        } catch { propertyManagement.value.propertyComments = 0 }

        // 15. 사용자 상세 관리 통계
        userManagement.value.total = stats.value.users
        userManagement.value.partners = partners
        userManagement.value.suspended = suspended
        userManagement.value.weekSignups = weekSignups
        userManagement.value.monthSignups = monthSignups

        // 역할별 카운트
        let adminCount = 0, userCount = 0, deletedCount = 0
        usersSnap.forEach(doc => {
          const d = doc.data()
          if (d.role === 'admin') adminCount++
          else if (d.role !== 'partner') userCount++
          if (d.isDeleted) deletedCount++
        })
        userManagement.value.admins = adminCount
        userManagement.value.users = userCount
        userManagement.value.deleted = deletedCount

        // 16. 수익 현황
        revenueStats.value.totalCharged = pointStats.value.totalCharged
        revenueStats.value.monthCharged = monthStats.value.chargedPoints
        revenueStats.value.jumpRevenue = jumpStats.value.totalRevenue

        // 캠페인 수익
        try {
          const campaignTxSnap = await getDocs(query(collection(db, 'pointTransactions'), where('type', '==', 'use')))
          let campaignRev = 0
          campaignTxSnap.forEach(doc => {
            const d = doc.data()
            if (d.description && d.description.includes('캠페인')) {
              campaignRev += Math.abs(d.amount || 0)
            }
          })
          revenueStats.value.campaignRevenue = campaignRev
        } catch { revenueStats.value.campaignRevenue = 0 }

      } catch (error) {
        console.error('통계 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    // 시간 포맷
    const formatTimeAgo = (dateStr) => {
      if (!dateStr) return ''
      const now = new Date()
      const date = new Date(dateStr)
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return '방금 전'
      if (diffMins < 60) return `${diffMins}분 전`
      if (diffHours < 24) return `${diffHours}시간 전`
      if (diffDays < 7) return `${diffDays}일 전`
      return date.toLocaleDateString('ko-KR')
    }

    onMounted(() => {
      fetchAllStats()
    })

    return {
      loading,
      todayDate,
      stats,
      today,
      weekStats,
      monthStats,
      pending,
      totalPending,
      consultationStats,
      pointStats,
      jumpStats,
      adStats,
      partnerManagement,
      recentPartnerApps,
      serviceManagement,
      support,
      recentInquiries,
      communityManagement,
      propertyManagement,
      userManagement,
      revenueStats,
      formatNumber,
      formatTimeAgo
    }
  }
}
</script>
